// pages/admin/CashOnDelivery.tsx
import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
  Drawer,
  CircularProgress,
  Alert,
  Snackbar,
  Button,
  TextField,
  InputAdornment,
  Card,
  Chip,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Tooltip,
  Switch,
  FormControlLabel,
  Divider,
  Pagination,
} from '@mui/material';
import {
  Refresh,
  Search,
  Add,
  Edit,
  Delete,
  Close,
  Save,
  CreditCard,
  CheckCircle,
  Cancel,
  Settings as SettingsIcon,
  AttachMoney,
  Payments,
  Info,
  ToggleOn,
  ToggleOff,
} from '@mui/icons-material';

import Slidemenu from '../../components/admin/adminSlidemenu.tsx';
import AdminHeader from '../../components/admin/adminHeader.tsx';

import {
  useListPaymentMethodsQuery,
  useAddPaymentMethodMutation,
  useUpdatePaymentMethodMutation,
  useDeletePaymentMethodMutation,
  PaymentMethod,
} from '../../features/admin/caseOnDeliveryApi.ts';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatCurrency = (amount: string | number): string => {
  const num = typeof amount === 'string' ? parseFloat(amount) : Number(amount);
  return `AED ${(num || 0).toFixed(2)}`;
};

const formatDate = (dateStr?: string | null) => {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString('en-AE', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch { return dateStr; }
};

// ─── Form state ───────────────────────────────────────────────────────────────

interface MethodForm {
  method_name: string;
  code: string;
  status: 0 | 1;
  extra_charge: string;
}

const EMPTY_FORM: MethodForm = { method_name: '', code: '', status: 1, extra_charge: '0' };

// ─────────────────────────────────────────────────────────────────────────────
// Add / Edit Dialog
// ─────────────────────────────────────────────────────────────────────────────

interface MethodDialogProps {
  open: boolean;
  editTarget: PaymentMethod | null;
  onClose: () => void;
  onSaved: (msg: string) => void;
  onError: (msg: string) => void;
}

const MethodDialog: React.FC<MethodDialogProps> = ({
  open, editTarget, onClose, onSaved, onError,
}) => {
  const [form, setForm] = useState<MethodForm>(EMPTY_FORM);
  const [addPaymentMethod, { isLoading: adding }] = useAddPaymentMethodMutation();
  const [updatePaymentMethod, { isLoading: updating }] = useUpdatePaymentMethodMutation();
  const isSaving = adding || updating;

  useEffect(() => {
    if (open) {
      setForm(editTarget ? {
        method_name: editTarget.method_name,
        code: editTarget.code,
        status: editTarget.status,
        extra_charge: String(editTarget.extra_charge ?? 0),
      } : EMPTY_FORM);
    }
  }, [open, editTarget]);

  const handleSubmit = async () => {
    if (!form.method_name.trim()) { onError('Method name is required'); return; }
    if (!form.code.trim()) { onError('Code is required (e.g. COD, ONLINE, WALLET)'); return; }
    
    try {
      if (editTarget) {
        const res = await updatePaymentMethod({
          id: editTarget.id,
          method_name: form.method_name.trim(),
          code: form.code.trim().toUpperCase(),
          status: form.status,
          extra_charge: parseFloat(form.extra_charge) || 0,
        }).unwrap();
        onSaved(res.message || 'Payment method updated successfully');
      } else {
        const res = await addPaymentMethod({
          method_name: form.method_name.trim(),
          code: form.code.trim().toUpperCase(),
          status: form.status,
          extra_charge: parseFloat(form.extra_charge) || 0,
        }).unwrap();
        onSaved(res.message || 'Payment method added successfully');
      }
      onClose();
    } catch (err: any) {
      onError(err?.data?.message || err?.message || 'Operation failed');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: '14px' } }}>
      <DialogTitle sx={{ pb: 0, pt: 2.5, px: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ bgcolor: '#e3f2fd', borderRadius: '8px', p: 0.8, display: 'flex' }}>
              <CreditCard sx={{ color: '#1565c0', fontSize: 20 }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#202223' }}>
              {editTarget ? 'Edit Payment Method' : 'Add Payment Method'}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small"><Close /></IconButton>
        </Box>
      </DialogTitle>
      <Divider sx={{ mt: 2 }} />
      <DialogContent sx={{ px: 3, pt: 2.5, pb: 1 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <TextField
            label="Method Name"
            placeholder="e.g. Cash on Delivery"
            value={form.method_name}
            onChange={e => setForm(f => ({ ...f, method_name: e.target.value }))}
            fullWidth
            size="small"
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
          />
          <TextField
            label="Code"
            placeholder="e.g. COD, ONLINE, WALLET"
            value={form.code}
            onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))}
            fullWidth
            size="small"
            helperText="Unique uppercase identifier for this method"
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
            inputProps={{ style: { fontFamily: 'monospace', letterSpacing: '1px', fontWeight: 600 } }}
          />
          <TextField
            label="Extra Charge (AED)"
            type="number"
            value={form.extra_charge}
            onChange={e => setForm(f => ({ ...f, extra_charge: e.target.value }))}
            fullWidth
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>AED</Typography>
                </InputAdornment>
              ),
            }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
          />
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1, 
            p: 1.5, 
            bgcolor: '#fafbfb', 
            borderRadius: '8px', 
            border: '1px solid #e1e3e5' 
          }}>
            <FormControlLabel
              control={
                <Switch 
                  checked={form.status === 1}
                  onChange={e => setForm(f => ({ ...f, status: e.target.checked ? 1 : 0 }))}
                  sx={{ 
                    '& .Mui-checked': { color: '#008060' }, 
                    '& .Mui-checked + .MuiSwitch-track': { bgcolor: '#008060' } 
                  }}
                />
              }
              label={
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {form.status === 1 ? 'Active' : 'Inactive'}
                </Typography>
              }
            />
            <Chip
              label={form.status === 1 ? 'Enabled' : 'Disabled'}
              size="small"
              sx={{ 
                bgcolor: form.status === 1 ? '#d1f4e0' : '#ffebee', 
                color: form.status === 1 ? '#00783e' : '#c62828', 
                fontWeight: 600, 
                fontSize: '11px' 
              }}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2.5, gap: 1 }}>
        <Button 
          onClick={onClose} 
          variant="outlined"
          sx={{ 
            textTransform: 'none', 
            borderColor: '#c9cccf', 
            color: '#202223', 
            borderRadius: '8px', 
            fontWeight: 500 
          }}
        >
          Cancel
        </Button>
        <Button 
          variant="contained" 
          onClick={handleSubmit} 
          disabled={isSaving}
          startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <Save />}
          sx={{ 
            textTransform: 'none', 
            bgcolor: '#202223', 
            borderRadius: '8px', 
            fontWeight: 500, 
            '&:hover': { bgcolor: '#000' } 
          }}
        >
          {isSaving ? 'Saving...' : editTarget ? 'Update' : 'Add Method'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Delete Confirm Dialog
// ─────────────────────────────────────────────────────────────────────────────

interface DeleteDialogProps {
  target: PaymentMethod | null;
  onClose: () => void;
  onDeleted: (msg: string) => void;
  onError: (msg: string) => void;
}

const DeleteDialog: React.FC<DeleteDialogProps> = ({ target, onClose, onDeleted, onError }) => {
  const [deletePaymentMethod, { isLoading }] = useDeletePaymentMethodMutation();

  const handleDelete = async () => {
    if (!target) return;
    try {
      const res = await deletePaymentMethod({ id: target.id }).unwrap();
      onDeleted(res.message || 'Deleted successfully');
      onClose();
    } catch (err: any) {
      onError(err?.data?.message || err?.message || 'Failed to delete');
    }
  };

  return (
    <Dialog open={!!target} onClose={onClose} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: '12px' } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#202223' }}>Delete Payment Method</Typography>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Are you sure you want to delete this payment method? This action cannot be undone.
        </Typography>
        {target && (
          <Box sx={{ p: 2, bgcolor: '#ffebee', borderRadius: '8px', border: '1px solid #ffcdd2' }}>
            <Typography variant="body2" sx={{ fontWeight: 700, color: '#c62828' }}>{target.method_name}</Typography>
            <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#c62828' }}>Code: {target.code}</Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button 
          onClick={onClose} 
          variant="outlined" 
          size="small"
          sx={{ textTransform: 'none', borderColor: '#c9cccf', color: '#202223', borderRadius: '7px' }}
        >
          Cancel
        </Button>
        <Button 
          variant="contained" 
          size="small" 
          disabled={isLoading} 
          onClick={handleDelete}
          startIcon={isLoading ? <CircularProgress size={14} color="inherit" /> : <Delete />}
          sx={{ textTransform: 'none', bgcolor: '#c62828', borderRadius: '7px', '&:hover': { bgcolor: '#b71c1c' } }}
        >
          {isLoading ? 'Deleting...' : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// COD Settings Tab
// ─────────────────────────────────────────────────────────────────────────────

interface CodSettingsTabProps {
  methods: PaymentMethod[];
  isLoading: boolean;
  onSnackbar: (msg: string, sev: 'success' | 'error') => void;
}

const CodSettingsTab: React.FC<CodSettingsTabProps> = ({ methods, isLoading, onSnackbar }) => {
  const [updatePaymentMethod, { isLoading: saving }] = useUpdatePaymentMethodMutation();

  const codMethod = useMemo(() => methods.find(m => m.code === 'COD') || null, [methods]);

  const [codEnabled, setCodEnabled] = useState(true);
  const [extraCharge, setExtraCharge] = useState('0');
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (codMethod) {
      setCodEnabled(codMethod.status === 1);
      setExtraCharge(String(codMethod.extra_charge ?? 0));
      setHasChanges(false);
    }
  }, [codMethod]);

  useEffect(() => {
    if (!codMethod) return;
    setHasChanges(
      codEnabled !== (codMethod.status === 1) ||
      extraCharge !== String(codMethod.extra_charge ?? 0)
    );
  }, [codEnabled, extraCharge, codMethod]);

  const handleReset = () => {
    if (codMethod) {
      setCodEnabled(codMethod.status === 1);
      setExtraCharge(String(codMethod.extra_charge ?? 0));
      setHasChanges(false);
    }
  };

  const handleSave = async () => {
    if (!codMethod) { 
      onSnackbar('No COD payment method found. Please add one first.', 'error'); 
      return; 
    }
    try {
      const res = await updatePaymentMethod({
        id: codMethod.id,
        status: codEnabled ? 1 : 0,
        extra_charge: parseFloat(extraCharge) || 0,
      }).unwrap();
      onSnackbar(res.message || 'COD settings updated successfully!', 'success');
      setHasChanges(false);
    } catch (err: any) {
      onSnackbar(err?.data?.message || err?.message || 'Failed to update COD settings', 'error');
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={30} sx={{ color: '#e65100' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {!codMethod && (
        <Alert severity="warning" icon={<Info />} sx={{ mb: 3, borderRadius: '10px' }}>
          No payment method with code <strong>COD</strong> found. Go to the <em>All Methods</em> tab and add one to manage COD settings here.
        </Alert>
      )}

      <Card sx={{ borderRadius: '12px', border: '1px solid #e1e3e5', boxShadow: 'none', mb: 3 }}>
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
            <Box sx={{ bgcolor: '#fff3e0', borderRadius: '8px', p: 1, display: 'flex' }}>
              <Payments sx={{ color: '#e65100', fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#202223' }}>Cash on Delivery (COD)</Typography>
              <Typography variant="body2" color="text.secondary">
                Control whether COD is available to customers at checkout and set the extra charge.
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 2.5 }} />

          {codMethod && (
            <Box sx={{ bgcolor: '#f6f6f7', p: 2, borderRadius: '8px', mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Info sx={{ color: '#2196f3', fontSize: 20 }} />
              <Typography variant="body2" color="text.secondary">
                Current status:&nbsp;
                <strong style={{ color: codMethod.status === 1 ? '#00783e' : '#c62828' }}>
                  {codMethod.status === 1 ? 'Active (Available at checkout)' : 'Inactive (Hidden from checkout)'}
                </strong>
                &nbsp;·&nbsp;Extra charge:&nbsp;
                <strong>{formatCurrency(codMethod.extra_charge)}</strong>
              </Typography>
            </Box>
          )}

          <Box sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            p: 2, bgcolor: codEnabled ? '#f0fdf4' : '#fafbfb',
            borderRadius: '10px', border: `1px solid ${codEnabled ? '#86efac' : '#e1e3e5'}`,
            mb: 2.5, transition: 'all 0.2s ease',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {codEnabled
                ? <ToggleOn sx={{ color: '#008060', fontSize: 28 }} />
                : <ToggleOff sx={{ color: '#6d7175', fontSize: 28 }} />
              }
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#202223' }}>COD Availability</Typography>
                <Typography variant="caption" color="text.secondary">
                  {codEnabled
                    ? 'Customers can choose COD at checkout'
                    : 'COD is currently hidden from checkout'}
                </Typography>
              </Box>
            </Box>
            <FormControlLabel
              control={
                <Switch 
                  checked={codEnabled} 
                  disabled={!codMethod}
                  onChange={e => setCodEnabled(e.target.checked)}
                  sx={{ 
                    '& .Mui-checked': { color: '#008060' }, 
                    '& .Mui-checked + .MuiSwitch-track': { bgcolor: '#008060' } 
                  }}
                />
              }
              label={
                <Chip 
                  label={codEnabled ? 'Active' : 'Inactive'} 
                  size="small"
                  sx={{
                    bgcolor: codEnabled ? '#d1f4e0' : '#ffebee',
                    color: codEnabled ? '#00783e' : '#c62828',
                    fontWeight: 600, 
                    fontSize: '11px',
                  }}
                />
              }
            />
          </Box>

          <Box sx={{ maxWidth: 280 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#202223', mb: 1 }}>Extra Charge for COD</Typography>
            <TextField
              value={extraCharge}
              onChange={e => { 
                const v = e.target.value; 
                if (v === '' || /^\d*\.?\d*$/.test(v)) setExtraCharge(v); 
              }}
              size="small" 
              fullWidth 
              disabled={!codMethod} 
              placeholder="0.00"
              helperText="Additional fee charged when customer selects COD"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#6d7175' }}>AED</Typography>
                  </InputAdornment>
                ),
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: 'white' } }}
            />
          </Box>
        </Box>
      </Card>

      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button 
          onClick={handleReset} 
          disabled={!hasChanges || saving}
          sx={{ textTransform: 'none', fontWeight: 600, color: '#202223', borderRadius: '8px', px: 2.5 }}
        >
          Reset
        </Button>
        <Button 
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />}
          onClick={handleSave}
          disabled={!hasChanges || saving || !codMethod}
          sx={{
            bgcolor: '#008060', 
            textTransform: 'none', 
            fontWeight: 600, 
            borderRadius: '8px', 
            px: 2.5,
            '&:hover': { bgcolor: '#006e52' },
            '&.Mui-disabled': { bgcolor: '#c9cccf', color: '#8c9196' }
          }}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </Box>
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────

const CashOnDelivery = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [searchStr, setSearchStr] = useState('');
  const [page, setPage] = useState(1);
  const rowsPerPage = 10;

  const [snackbar, setSnackbar] = useState({
    open: false, message: '', severity: 'success' as 'success' | 'error',
  });

  const [methodDialogOpen, setMethodDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<PaymentMethod | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PaymentMethod | null>(null);
  const [togglingId, setTogglingId] = useState<number | null>(null);

  const { data: listResponse, isLoading, isError, refetch } = useListPaymentMethodsQuery();
  const [updatePaymentMethod] = useUpdatePaymentMethodMutation();

  const methods: PaymentMethod[] = listResponse?.data || [];

  const filtered = useMemo(() => {
    if (!searchStr.trim()) return methods;
    const s = searchStr.toLowerCase();
    return methods.filter(m =>
      m.method_name.toLowerCase().includes(s) ||
      m.code.toLowerCase().includes(s)
    );
  }, [methods, searchStr]);

  const totalPages = Math.ceil(filtered.length / rowsPerPage);
  const paginatedItems = filtered.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  const showSnackbar = (message: string, severity: 'success' | 'error') =>
    setSnackbar({ open: true, message, severity });

  const handleRefresh = async () => {
    try { 
      await refetch(); 
      showSnackbar('Refreshed!', 'success'); 
    } catch { 
      showSnackbar('Error refreshing', 'error'); 
    }
  };

  const handleToggleStatus = async (m: PaymentMethod) => {
    setTogglingId(m.id);
    try {
      const res = await updatePaymentMethod({ 
        id: m.id, 
        status: m.status === 1 ? 0 : 1 
      }).unwrap();
      showSnackbar(res.message || 'Status updated', 'success');
    } catch (err: any) {
      showSnackbar(err?.data?.message || 'Failed to toggle status', 'error');
    } finally { 
      setTogglingId(null); 
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f1f2f4' }}>
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <Box sx={{ width: '250px', flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
          <Slidemenu mobileOpen={false} onToggle={() => setMobileOpen(v => !v)} />
        </Box>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': { width: 250 } }}
        >
          <Slidemenu onToggle={() => setMobileOpen(false)} mobileOpen={false} />
        </Drawer>

        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
          <AdminHeader onToggleMenu={() => setMobileOpen(v => !v)} />

          <Box sx={{ flexGrow: 1, overflow: 'auto', p: { xs: 2, md: 3 } }}>
            <Box sx={{ maxWidth: 1600, mx: 'auto' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#202223' }}>Payment Methods</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.3 }}>
                    Manage checkout payment options and Cash on Delivery settings
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button 
                    variant="outlined" 
                    startIcon={<Refresh />} 
                    onClick={handleRefresh} 
                    disabled={isLoading}
                    sx={{ 
                      textTransform: 'none', 
                      borderColor: '#c9cccf', 
                      color: '#202223', 
                      borderRadius: '8px', 
                      fontWeight: 500 
                    }}
                  >
                    Refresh
                  </Button>
                  <Button 
                    variant="contained" 
                    startIcon={<Add />} 
                    onClick={() => { setEditTarget(null); setMethodDialogOpen(true); }}
                    sx={{ 
                      textTransform: 'none', 
                      bgcolor: '#202223', 
                      borderRadius: '8px', 
                      fontWeight: 500, 
                      '&:hover': { bgcolor: '#000' } 
                    }}
                  >
                    Add Method
                  </Button>
                </Box>
              </Box>

              {!isLoading && !isError && (
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  {[
                    { icon: <CreditCard sx={{ color: '#1565c0', fontSize: 24 }} />, bg: '#e3f2fd', value: methods.length, label: 'Total Methods' },
                    { icon: <CheckCircle sx={{ color: '#2e7d32', fontSize: 24 }} />, bg: '#e8f5e9', value: methods.filter(m => m.status === 1).length, label: 'Active Methods' },
                    { icon: <Cancel sx={{ color: '#c62828', fontSize: 24 }} />, bg: '#ffebee', value: methods.filter(m => m.status === 0).length, label: 'Inactive Methods' },
                    { icon: <AttachMoney sx={{ color: '#e65100', fontSize: 24 }} />, bg: '#fff3e0', value: formatCurrency(methods.reduce((sum, m) => sum + (parseFloat(String(m.extra_charge)) || 0), 0)), label: 'Total Extra Charges', isStr: true },
                  ].map(({ icon, bg, value, label, isStr }) => (
                    <Grid item xs={12} sm={6} md={3} key={label}>
                      <Card sx={{ borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5' }}>
                        <Box sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box sx={{ bgcolor: bg, borderRadius: '8px', p: 1, display: 'flex' }}>{icon}</Box>
                          <Box>
                            <Typography variant={isStr ? 'h6' : 'h5'} sx={{ fontWeight: 700, color: '#202223', fontSize: isStr ? '18px' : undefined }}>
                              {value}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">{label}</Typography>
                          </Box>
                        </Box>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}

              <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5', overflow: 'hidden' }}>
                <Box sx={{ borderBottom: '1px solid #e1e3e5' }}>
                  <Tabs 
                    value={tabValue} 
                    onChange={(_, v) => { setTabValue(v); setPage(1); setSearchStr(''); }}
                    sx={{
                      px: 2,
                      '& .MuiTab-root': { textTransform: 'none', fontWeight: 500, fontSize: '14px', minHeight: 48, color: '#6d7175' },
                      '& .Mui-selected': { color: '#202223', fontWeight: 600 },
                      '& .MuiTabs-indicator': { backgroundColor: '#202223', height: 3 },
                    }}
                  >
                    <Tab label={`All Methods (${methods.length})`} />
                    <Tab label="COD Settings" icon={<SettingsIcon sx={{ fontSize: 16 }} />} iconPosition="start" />
                  </Tabs>
                </Box>

                {tabValue === 0 && (
                  <>
                    <Box sx={{ p: 2, borderBottom: '1px solid #e1e3e5', display: 'flex', gap: 1 }}>
                      <TextField 
                        size="small" 
                        placeholder="Search by method name or code..."
                        value={searchStr} 
                        onChange={e => { setSearchStr(e.target.value); setPage(1); }}
                        sx={{ maxWidth: 380, '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: 'white' } }}
                        InputProps={{ 
                          startAdornment: <InputAdornment position="start"><Search fontSize="small" sx={{ color: '#6d7175' }} /></InputAdornment> 
                        }}
                      />
                      {searchStr && (
                        <Button 
                          size="small" 
                          variant="outlined" 
                          onClick={() => { setSearchStr(''); setPage(1); }}
                          sx={{ textTransform: 'none', borderColor: '#c9cccf', color: '#202223', borderRadius: '8px' }}
                        >
                          Clear
                        </Button>
                      )}
                    </Box>

                    {isLoading && (
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300, bgcolor: 'white' }}>
                        <CircularProgress size={32} sx={{ color: '#008060' }} />
                      </Box>
                    )}

                    {isError && !isLoading && (
                      <Box sx={{ p: 3, bgcolor: 'white' }}>
                        <Alert severity="error" action={<Button size="small" onClick={handleRefresh}>Retry</Button>}>
                          Failed to load payment methods. Please try again.
                        </Alert>
                      </Box>
                    )}

                    {!isLoading && !isError && paginatedItems.length === 0 && (
                      <Box sx={{ textAlign: 'center', py: 8, bgcolor: 'white' }}>
                        <CreditCard sx={{ fontSize: 64, color: '#c9cccf', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 500 }}>
                          {searchStr ? `No results for "${searchStr}"` : 'No payment methods yet'}
                        </Typography>
                        {!searchStr && (
                          <Button 
                            variant="contained" 
                            startIcon={<Add />}
                            onClick={() => { setEditTarget(null); setMethodDialogOpen(true); }}
                            sx={{ mt: 2, textTransform: 'none', bgcolor: '#202223', borderRadius: '8px', '&:hover': { bgcolor: '#000' } }}
                          >
                            Add First Method
                          </Button>
                        )}
                      </Box>
                    )}

                    {!isLoading && !isError && paginatedItems.length > 0 && (
                      <Box sx={{ bgcolor: 'white', overflowX: 'auto' }}>
                        <Box sx={{ minWidth: 800 }}>
                          <Box sx={{
                            display: 'grid',
                            gridTemplateColumns: '60px 1fr 120px 110px 130px 120px 100px',
                            gap: 1.5, px: 2, py: 1.5,
                            borderBottom: '1px solid #e1e3e5', bgcolor: '#fafbfb',
                          }}>
                            {['ID', 'Method Name', 'Code', 'Status', 'Extra Charge', 'Created', 'Actions'].map(h => (
                              <Typography key={h} variant="caption" sx={{ color: '#6d7175', fontWeight: 700, fontSize: '11px', textTransform: 'uppercase' }}>
                                {h}
                              </Typography>
                            ))}
                          </Box>

                          {paginatedItems.map(m => (
                            <Box key={m.id} sx={{
                              display: 'grid',
                              gridTemplateColumns: '60px 1fr 120px 110px 130px 120px 100px',
                              gap: 1.5, px: 2, py: 2, alignItems: 'center',
                              borderBottom: '1px solid #e1e3e5',
                              '&:last-child': { borderBottom: 0 },
                              '&:hover': { bgcolor: '#f9fafb' },
                              transition: 'background-color 0.15s ease',
                            }}>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#6d7175' }}>#{m.id}</Typography>

                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ width: 32, height: 32, borderRadius: '8px', bgcolor: '#e3f2fd', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                  <CreditCard sx={{ fontSize: 16, color: '#1565c0' }} />
                                </Box>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#202223' }}>{m.method_name}</Typography>
                              </Box>

                              <Chip 
                                label={m.code} 
                                size="small"
                                sx={{ bgcolor: '#f3e5f5', color: '#7b1fa2', fontWeight: 700, fontSize: '11px', height: '24px', fontFamily: 'monospace', letterSpacing: '0.5px', width: 'fit-content' }}
                              />

                              <Tooltip title={m.status === 1 ? 'Click to deactivate' : 'Click to activate'}>
                                <Box onClick={() => handleToggleStatus(m)} sx={{ cursor: 'pointer', display: 'inline-flex' }}>
                                  {togglingId === m.id
                                    ? <CircularProgress size={18} sx={{ color: '#008060' }} />
                                    : (
                                      <Chip
                                        icon={m.status === 1 ? <CheckCircle sx={{ fontSize: '13px !important' }} /> : <Cancel sx={{ fontSize: '13px !important' }} />}
                                        label={m.status === 1 ? 'Active' : 'Inactive'}
                                        size="small"
                                        sx={{
                                          bgcolor: m.status === 1 ? '#d1f4e0' : '#ffebee',
                                          color: m.status === 1 ? '#00783e' : '#c62828',
                                          fontWeight: 600, fontSize: '11px', height: '24px', cursor: 'pointer',
                                          '&:hover': { opacity: 0.85 },
                                        }}
                                      />
                                    )
                                  }
                                </Box>
                              </Tooltip>

                              <Typography variant="body2" sx={{ fontWeight: 700, color: '#7b1fa2' }}>
                                {formatCurrency(m.extra_charge)}
                              </Typography>

                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12px' }}>
                                {formatDate(m.createddt)}
                              </Typography>

                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                <Tooltip title="Edit">
                                  <IconButton 
                                    size="small" 
                                    onClick={() => { setEditTarget(m); setMethodDialogOpen(true); }}
                                    sx={{ color: '#1565c0', bgcolor: '#e3f2fd', borderRadius: '6px', p: 0.7, '&:hover': { bgcolor: '#bbdefb' } }}
                                  >
                                    <Edit sx={{ fontSize: 15 }} />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete">
                                  <IconButton 
                                    size="small" 
                                    onClick={() => setDeleteTarget(m)}
                                    sx={{ color: '#c62828', bgcolor: '#ffebee', borderRadius: '6px', p: 0.7, '&:hover': { bgcolor: '#ffcdd2' } }}
                                  >
                                    <Delete sx={{ fontSize: 15 }} />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}

                    {totalPages > 1 && !isLoading && (
                      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2, bgcolor: 'white', borderTop: '1px solid #e1e3e5' }}>
                        <Pagination 
                          count={totalPages} 
                          page={page} 
                          onChange={(_, p) => setPage(p)}
                          sx={{ 
                            '& .MuiPaginationItem-root': { borderRadius: '8px', fontWeight: 500 }, 
                            '& .Mui-selected': { bgcolor: '#202223 !important', color: 'white' } 
                          }}
                        />
                      </Box>
                    )}
                  </>
                )}

                {tabValue === 1 && (
                  <CodSettingsTab methods={methods} isLoading={isLoading} onSnackbar={showSnackbar} />
                )}
              </Card>
            </Box>
          </Box>
        </Box>
      </Box>

      <MethodDialog
        open={methodDialogOpen}
        editTarget={editTarget}
        onClose={() => setMethodDialogOpen(false)}
        onSaved={msg => showSnackbar(msg, 'success')}
        onError={msg => showSnackbar(msg, 'error')}
      />
      
      <DeleteDialog
        target={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onDeleted={msg => showSnackbar(msg, 'success')}
        onError={msg => showSnackbar(msg, 'error')}
      />

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={5000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          severity={snackbar.severity} 
          variant="filled" 
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CashOnDelivery;